import makeStyles from '@mui/styles/makeStyles';
import {isEmpty, trim} from 'lodash';
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link} from 'react-router-dom';
import {color, font, type} from "common/tokens";
import useBusy from "common/useBusy";
import Question from "components/Question";
import AuthLayout from "components/redesign/AuthLayout";
import AuthSplash from "components/redesign/AuthSplash";
import CtaButton from "components/redesign/CtaButton";
import UnderMaintenance from "views/UnderMaintenance";
import EmailIcon from "./emailIcon.svg";
import GoogleLogo from "./googleLogo.svg";

const useStyles = makeStyles({
  title: {
    ...type.panelTitle,
    color: color.TEXT,
    margin: 0,
    textAlign: 'center',
  },
  subtitle: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '6px 0 20px',
    textAlign: 'center',
  },
  // Shown when someone arrives from the Mentor To Go app already registered
  // under a different address.
  notice: {
    ...type.meta,
    fontWeight: font.REGULAR,
    background: color.ACCENT_TINT,
    color: color.ACCENT_TEXT,
    borderRadius: 12,
    padding: '12px 14px',
    margin: '0 0 22px',
  },
  forgot: {
    ...type.meta,
    fontWeight: font.SEMIBOLD,
    color: color.ACCENT_TEXT,
    textDecoration: 'none',
    alignSelf: 'flex-end',
    margin: '-14px 0 20px',
  },
  error: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.DANGER,
    textAlign: 'center',
    margin: '14px 0 0',
  },
  or: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '24px 0 18px',
    '&::before, &::after': {
      content: '""',
      flex: 1,
      height: 1,
      background: color.DIVIDER,
    },
  },
  orText: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_SUBTLE,
    margin: 0,
  },
  alternatives: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  mark: {
    width: 16,
    height: 16,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    '& > div': {
      marginBottom: 18,
    },
  },
});

const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const SignIn = ({emailSignIn, googleSignIn, loginError, loginErrorMessage, history, resetErrorMessage, inMaintenance = false, isVerifying = false}) => {
  const classes = useStyles();
  const {t} = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [ncsEmail, setNcsEmail] = useState(null);
  const [busy, run] = useBusy();

  useEffect(() => {
    resetErrorMessage();
  }, []);

  // The app hands the address over in localStorage and it is only relevant to
  // this visit, so it is cleared as soon as the tab goes.
  useEffect(() => {
    setNcsEmail(localStorage.getItem("ncsEmail"));

    const clear = () => localStorage.removeItem("ncsEmail");
    window.addEventListener("beforeunload", clear);
    return () => window.removeEventListener("beforeunload", clear);
  }, []);

  const handleSubmit = event => {
    if (event) event.preventDefault();

    const emailIsValid = emailPattern.test(String(email).toLowerCase());
    const passwordIsValid = !isEmpty(password);

    setEmailError(!emailIsValid);
    setPasswordError(!passwordIsValid);

    if (emailIsValid && passwordIsValid) {
      run(() => emailSignIn(email, password));
    }
  };

  if (inMaintenance) {
    return <UnderMaintenance/>;
  }

  // An already signed-in reader is on their way to their own screen; the form
  // would only flash at them on the way past.
  if (isVerifying) {
    return <AuthSplash/>;
  }

  return (
    <AuthLayout>
      <h1 className={classes.title}>{t('signinTitle')}</h1>
      <p className={classes.subtitle}>{t('signinSubtitle')}</p>

      {Boolean(ncsEmail) && (
        <p className={classes.notice}>
          {`Hello, you are already registered at the Mentor To Go app with ${ncsEmail}. Please use your existing account to log in.`}
        </p>
      )}

      {/* A real form, so Enter submits from either field. */}
      <form className={classes.form} onSubmit={handleSubmit}>
        <Question
          question={{label: t('emailLabel'), questionType: 'Text', autocomplete: 'email', mandatory: true}}
          onAnswered={value => setEmail(trim(value.toLowerCase()))}
          value={email}
          error={emailError && t('invalidEmail')}
        />
        <Question
          question={{label: t('passwordLabelCaps'), questionType: 'Password', mandatory: true}}
          onAnswered={setPassword}
          value={password}
          error={passwordError && t('passwordRequired')}
        />

        <Link to={'/resetpassword'} className={classes.forgot}>{t('forgotPassword')}</Link>

        <CtaButton type={'submit'} size={'large'} fullWidth label={t('submit')} loading={busy}/>
      </form>
      {loginError && <p className={classes.error} role={'alert'}>{loginErrorMessage}</p>}

      <div className={classes.or}>
        <p className={classes.orText}>{t('or')}</p>
      </div>

      <div className={classes.alternatives}>
        <CtaButton
          size={'large'}
          variant={'secondary'}
          fullWidth
          label={t('signInViaGoogle')}
          icon={<img src={GoogleLogo} alt={''} className={classes.mark}/>}
          disabled={busy}
          onClick={() => googleSignIn(email, history)}
        />
        <CtaButton
          size={'large'}
          variant={'secondary'}
          fullWidth
          label={t('useEmail')}
          icon={<img src={EmailIcon} alt={''} className={classes.mark}/>}
          disabled={busy}
          onClick={() => history.push('/emailverify')}
        />
      </div>
    </AuthLayout>
  );
};

export default SignIn;
